import { useEffect, useRef, useState, type FunctionComponent } from "react";
import { EyeOutlined, SmileOutlined } from "@ant-design/icons";
import { Avatar, Button, Input, Modal, Popover, Space, Tooltip, Typography, message } from "antd";

import {
  CHALLENGE_POST_REACTIONS,
  MAX_CHALLENGE_POST_LENGTH,
  MAX_CHALLENGE_POST_TITLE_LENGTH,
  type ChallengeFeedPost,
  type ChallengePostMedia,
} from "@fitness-app/data-models/entities/ChallengeFeed";

import ChallengeFeedMarkdown from "../ChallengeFeedMarkdown";
import { ChallengePostMediaPreview, ChallengePostMediaUploadButton } from "../ChallengePostMediaField";

const DRAFT_PREFIX = "hollywood:challenge-post-draft";

type Draft = { title: string; content: string };

const draftKey = (challengeId: string) => `${DRAFT_PREFIX}:${challengeId}`;

const readDraft = (challengeId: string): Draft | null => {
  try {
    const raw = window.localStorage.getItem(draftKey(challengeId));
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      const { title, content } = parsed as Partial<Draft>;
      if (typeof title === "string" && typeof content === "string") {
        return { title, content };
      }
    }
    return null;
  } catch {
    return null;
  }
};

type Props = {
  open: boolean;
  challengeId: string;
  challengeName: string;
  authorName: string;
  authorAvatar?: string | null;
  /** Set when editing — the dialog doubles as the edit composer. */
  post: ChallengeFeedPost | null;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (values: { title: string | null; content: string; media: ChallengePostMedia[] }) => void;
};

/**
 * Post composer, modelled on the mailingr community composer: identity line ("X publikuje w …"),
 * an optional headline, the body, then a thin toolbar with the attachment/emoji actions and the
 * publish buttons. New posts keep a localStorage draft — losing a written-out announcement to a
 * stray Esc is the one failure mode that actually hurts here.
 */
const ChallengePostComposerModal: FunctionComponent<Props> = ({
  open,
  challengeId,
  challengeName,
  authorName,
  authorAvatar,
  post,
  saving,
  onCancel,
  onSubmit,
}) => {
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [media, setMedia] = useState<ChallengePostMedia[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const isEditing = Boolean(post);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (post) {
      setTitle(post.title ?? "");
      setContent(post.content);
      setMedia(post.media);
    } else {
      const draft = readDraft(challengeId);
      setTitle(draft?.title ?? "");
      setContent(draft?.content ?? "");
      setMedia([]);
    }
    setShowPreview(false);
  }, [open, post, challengeId]);

  // Drafts are for NEW posts only — an unsaved edit must not resurface as a fresh announcement.
  useEffect(() => {
    if (!open || isEditing) {
      return;
    }
    if (!title && !content) {
      window.localStorage.removeItem(draftKey(challengeId));
      return;
    }
    window.localStorage.setItem(draftKey(challengeId), JSON.stringify({ title, content }));
  }, [open, isEditing, challengeId, title, content]);

  const insertAtCursor = (text: string) => {
    const textarea = bodyRef.current;
    if (!textarea) {
      setContent((current) => current + text);
      return;
    }
    const start = textarea.selectionStart ?? content.length;
    const end = textarea.selectionEnd ?? content.length;
    setContent(`${content.slice(0, start)}${text}${content.slice(end)}`);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + text.length, start + text.length);
    });
  };

  const canSubmit = Boolean(content.trim()) || media.length > 0;

  const submit = () => {
    if (!canSubmit) {
      void message.warning("Post musi mieć treść albo załącznik");
      return;
    }
    onSubmit({ title: title.trim() || null, content, media });
    if (!isEditing) {
      window.localStorage.removeItem(draftKey(challengeId));
    }
  };

  return (
    <Modal
      open={open}
      width={720}
      footer={null}
      onCancel={onCancel}
      destroyOnClose
      title={
        <Space size={10} align="center">
          <Avatar size={32} src={authorAvatar ?? undefined}>
            {(authorName || "T").charAt(0).toUpperCase()}
          </Avatar>
          <Typography.Text style={{ fontWeight: 400 }}>
            <Typography.Text strong>{authorName || "Trener"}</Typography.Text> publikuje w{" "}
            <Typography.Text strong>{challengeName}</Typography.Text>
          </Typography.Text>
        </Space>
      }
    >
      <Input
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        placeholder="Tytuł (opcjonalnie)"
        maxLength={MAX_CHALLENGE_POST_TITLE_LENGTH}
        size="large"
        variant="borderless"
        style={{ fontSize: 18, fontWeight: 600, paddingInline: 0 }}
      />

      {showPreview ? (
        <div className="min-h-[220px] rounded-lg border border-[#f0f0f0] bg-[#fafafa] p-4">
          {title.trim() ? (
            <Typography.Title level={4} style={{ marginTop: 0 }}>
              {title}
            </Typography.Title>
          ) : null}
          {content.trim() ? (
            <ChallengeFeedMarkdown content={content} />
          ) : (
            <Typography.Text type="secondary">Nic jeszcze nie napisano.</Typography.Text>
          )}
        </div>
      ) : (
        <Input.TextArea
          ref={bodyRef}
          autoFocus
          value={content}
          onChange={(event) => setContent(event.target.value)}
          maxLength={MAX_CHALLENGE_POST_LENGTH}
          variant="borderless"
          autoSize={{ minRows: 8, maxRows: 18 }}
          placeholder="Napisz coś… Markdown działa: **pogrubienie**, listy, > cytat, [link](https://…)"
          style={{ paddingInline: 0 }}
        />
      )}

      <ChallengePostMediaPreview value={media} onChange={setMedia} disabled={saving} />

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-[#f0f0f0] pt-3">
        <Space size={2}>
          <ChallengePostMediaUploadButton
            challengeId={challengeId}
            value={media}
            onChange={setMedia}
            disabled={saving}
          />
          <Popover
            trigger="click"
            content={
              <Space size={2}>
                {CHALLENGE_POST_REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => insertAtCursor(emoji)}
                    className="rounded px-2 py-1 text-xl hover:bg-[#f5f5f5]"
                  >
                    {emoji}
                  </button>
                ))}
              </Space>
            }
          >
            <Tooltip title="Emoji">
              <Button type="text" icon={<SmileOutlined />} disabled={showPreview} />
            </Tooltip>
          </Popover>
          <Tooltip title={showPreview ? "Wróć do pisania" : "Podgląd — tak zobaczy to klientka"}>
            <Button
              type={showPreview ? "default" : "text"}
              icon={<EyeOutlined />}
              onClick={() => setShowPreview((current) => !current)}
            />
          </Tooltip>
        </Space>

        <Space size={8} align="center">
          {isEditing ? null : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Powiadomienie do uczestniczek wyjdzie w ciągu ~15 min
            </Typography.Text>
          )}
          <Button onClick={onCancel}>Anuluj</Button>
          <Button type="primary" loading={saving} disabled={!canSubmit} onClick={submit}>
            {isEditing ? "Zapisz" : "Opublikuj"}
          </Button>
        </Space>
      </div>
    </Modal>
  );
};

export default ChallengePostComposerModal;
